import { makeAutoObservable } from "mobx";

// API Layer (Infrastructure)
import { apiClient } from "@/shared/api/baseClient";
import { AuthApi } from "@/features/auth/api";
import { ListingsApi } from "@/features/listings/api";
import { GeoApi } from "@/features/geo/api";

// Feature Stores (Domain)
import { AuthStore } from "@/features/auth/store";
import { ListingsStore } from "@/features/listings/store";
import { GeoStore } from "@/features/geo/store";
import { UiStore } from "@/features/ui/store";

/**
 * RootStore - Composition Root for ALL MobX Stores
 *
 * Implements the Singleton + Dependency Injection pattern as specified
 * in the Frontend Refactoring Plan (Section 4.1).
 *
 * Responsibilities:
 * 1. Instantiate all API classes (single source of truth)
 * 2. Instantiate all feature stores with DI (inject `this` reference)
 * 3. Provide cross-store access via `this.root` pattern
 *
 * Usage:
 * - Components: const { authStore } = useStore();
 * - Stores: this.root.authStore.user
 * - APIs: this.root.api.listings.search()
 *
 * @example
 * // In a component
 * const { authStore, listingsStore } = useStore();
 *
 * @example
 * // In a store
 * const token = await this.root.authStore.getIdToken();
 * const data = await this.root.api.listings.search(params);
 */
export class RootStore {
  // =============================================
  // API Layer - API modules and client
  // =============================================
  readonly api = {
    client: apiClient,
    auth: new AuthApi(apiClient),
    listings: new ListingsApi(apiClient),
    geo: new GeoApi(apiClient),
  };

  // =============================================
  // Feature Stores - Receive `this` for cross-access
  // =============================================
  readonly authStore: AuthStore;
  readonly listingsStore: ListingsStore;
  readonly geoStore: GeoStore;
  readonly uiStore: UiStore;

  constructor() {
    // Inject RootStore reference into each feature store
    // This enables cross-store communication via this.root
    this.authStore = new AuthStore(this);
    this.listingsStore = new ListingsStore(this);
    this.geoStore = new GeoStore(this);
    this.uiStore = new UiStore(this);

    // Make the RootStore observable
    // Exclude 'api' from being observable (it's just a container)
    makeAutoObservable(this, {
      api: false,
    });
  }
}

/**
 * Singleton instance of RootStore
 *
 * Exported for direct access if needed, but typically accessed
 * via React Context through the useStore() hook.
 */
export const rootStore = new RootStore();
